import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
interface RegisterStep {
  id: number;
  title: string;
  component: React.ReactNode;
}
export function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    signUp
  } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: location.state?.email || sessionStorage.getItem('signupEmail') || '',
    password: '',
    businessName: '',
    website: '',
    role: 'Underwriting'
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };
  const validateStep = (step: number): boolean => {
    setError('');
    switch (step) {
      case 0:
        // Contact Info
        if (!formData.firstName.trim()) {
          setError('First name is required');
          return false;
        }
        if (!formData.lastName.trim()) {
          setError('Last name is required');
          return false;
        }
        if (!formData.email.trim()) {
          setError('Email is required');
          return false;
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
          setError('Please enter a valid email address');
          return false;
        }
        if (!formData.password) {
          setError('Password is required');
          return false;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }
        return true;
      case 1:
        // Business Info
        if (!formData.businessName.trim()) {
          setError('Business name is required');
          return false;
        }
        return true;
      default:
        return true;
    }
  };
  const handleNext = async () => {
    // Validate current step before proceeding
    if (!validateStep(currentStep)) {
      return;
    }
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final step - create account
      setIsLoading(true);
      setError('');
      try {
        await signUp(formData.email, formData.password);
        navigate('/invite');
      } catch (error: any) {
        console.error('Error completing registration:', error);
        // Handle specific Firebase errors
        let errorMessage = 'Error creating account. Please try again.';
        if (error.code === 'auth/email-already-in-use') {
          errorMessage = 'This email is already registered. Please sign in instead.';
          // Show a helpful message with a link to sign in
          setError(errorMessage);
          // Optionally redirect to sign in after a delay
          setTimeout(() => {
            navigate('/signin', {
              state: {
                email: formData.email
              }
            });
          }, 2000);
          return;
        } else if (error.code === 'auth/invalid-email') {
          errorMessage = 'Invalid email address.';
        } else if (error.code === 'auth/weak-password') {
          errorMessage = 'Password is too weak. Please use a stronger password.';
        } else if (error.code === 'auth/operation-not-allowed') {
          errorMessage = 'Email/password accounts are not enabled. Please contact support.';
        } else if (error.code === 'auth/network-request-failed') {
          errorMessage = 'Network error. Please check your connection and try again.';
        }
        setError(errorMessage);
      } finally {
        setIsLoading(false);
      }
    }
  };
  const ContactInfoStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-111">
      <h3 className="text-xl font-semibold mb-6" data-id="element-112">What's your contact info?</h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-113">
        You'll be the account admin when you're creating the account
      </p>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-id="element-114">
          {error}
        </div>}

      <div className="space-y-4" data-id="element-115">
        <div data-id="element-116">
          <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-117">
            First name
          </label>
          <input id="firstName" name="firstName" type="text" value={formData.firstName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-118" />
        </div>

        <div data-id="element-119">
          <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-120">
            Surname
          </label>
          <input id="lastName" name="lastName" type="text" value={formData.lastName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-121" />
        </div>

        <div data-id="element-122">
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-123">
            Current email address
          </label>
          <input id="email" name="email" type="email" value={formData.email} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-124" />
        </div>

        <div data-id="element-125">
          <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-126">
            Password
          </label>
          <input id="password" name="password" type="password" value={formData.password} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required minLength={6} placeholder="At least 6 characters" data-id="element-127" />
        </div>
      </div>

      <div className="mt-6" data-id="element-128">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-129">
          Next
        </button>
      </div>
    </div>;
  const BusinessInfoStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-130">
      <h3 className="text-xl font-semibold mb-6" data-id="element-131">Add your business details</h3>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-id="element-132">
          {error}
        </div>}

      <div className="space-y-4" data-id="element-133">
        <div data-id="element-134">
          <label htmlFor="businessName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-135">
            Business Name
          </label>
          <input id="businessName" name="businessName" type="text" value={formData.businessName} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" required data-id="element-136" />
        </div>

        <div data-id="element-137">
          <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-138">
            Website
          </label>
          <input id="website" name="website" type="text" value={formData.website} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="https://" data-id="element-139" />
        </div>
      </div>

      <div className="mt-6" data-id="element-140">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-141">
          Next
        </button>
      </div>
    </div>;
  const RoleSelectionStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-142">
      <h3 className="text-xl font-semibold mb-6" data-id="element-143">What's your role?</h3>
      <p className="text-sm text-gray-600 mb-6" data-id="element-144">
        Let's set up the platform for how you work.
      </p>

      <div data-id="element-145">
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-146">
          Select role
        </label>
        <select id="role" name="role" value={formData.role} onChange={handleInputChange} className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-147">
          <option value="Founder/C-suite" data-id="element-148">Founder/Executive</option>
          <option value="Finance" data-id="element-149">Finance</option>
          <option value="Underwriting" data-id="element-150">Underwriting</option>
          <option value="Captive Manager" data-id="element-151">Captive Manager</option>
          <option value="Other" data-id="element-152">Other</option>
        </select>
      </div>

      <div className="mt-6" data-id="element-153">
        <button onClick={handleNext} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out" data-id="element-154">
          Continue
        </button>
        <button onClick={handleNext} className="w-full text-gray-600 font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out mt-2 hover:bg-gray-50" data-id="element-155">
          Skip
        </button>
      </div>
    </div>;
  const FeasibilityOptionsStep = <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200 mb-6 max-w-md w-full" data-id="element-156">
      <h3 className="text-xl font-semibold mb-6" data-id="element-157">Choose your path</h3>

      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm" data-id="element-158">
          {error}
          {error.includes('already registered') && <div className="mt-2" data-id="element-159">
              <a href="/signin" className="text-blue-600 hover:text-blue-800 font-medium underline" data-id="element-160">
                Go to Sign In →
              </a>
            </div>}
        </div>}

      <div className="space-y-4" data-id="element-161">
        <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-colors" data-id="element-162">
          <h4 className="font-medium" data-id="element-163">1. Fast-track feasibility study</h4>
          <p className="text-sm text-gray-600" data-id="element-164">
            Quick assessment of your MGA opportunity
          </p>
        </div>
        <div className="border border-gray-200 rounded-lg p-4 hover:border-blue-500 cursor-pointer transition-colors" data-id="element-165">
          <h4 className="font-medium" data-id="element-166">2. More Information</h4>
          <p className="text-sm text-gray-600" data-id="element-167">
            Detailed analysis with comprehensive insights
          </p>
        </div>
      </div>

      <div className="mt-6" data-id="element-168">
        <button onClick={handleNext} disabled={isLoading} className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed text-white font-medium py-3 px-4 rounded-md transition duration-150 ease-in-out flex items-center justify-center" data-id="element-169">
          {isLoading ? <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-170">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-171"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-172"></path>
              </svg>
              Creating account...
            </> : 'Continue'}
        </button>
      </div>
    </div>;
  const steps: RegisterStep[] = [{
    id: 0,
    title: 'Contact Information',
    component: ContactInfoStep
  }, {
    id: 1,
    title: 'Business Details',
    component: BusinessInfoStep
  }, {
    id: 2,
    title: 'Role Selection',
    component: RoleSelectionStep
  }, {
    id: 3,
    title: 'Feasibility Options',
    component: FeasibilityOptionsStep
  }];
  return <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4" data-id="element-173">
      <div className="w-full max-w-4xl" data-id="element-174">
        <div className="text-center mb-10" data-id="element-175">
          <h1 className="text-4xl font-bold mb-2" data-id="element-176">Register Journey</h1>
          <p className="text-gray-600" data-id="element-177">Complete your profile to get started</p>
        </div>

        <div className="flex flex-col items-center" data-id="element-178">
          {/* Progress indicator */}
          <div className="w-full max-w-md mb-8" data-id="element-179">
            <div className="flex justify-between" data-id="element-180">
              {steps.map((step, index) => <div key={step.id} className="flex flex-col items-center" data-id="element-181">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${index <= currentStep ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'}`} data-id="element-182">
                    {index < currentStep ? '✓' : index + 1}
                  </div>
                  {index < steps.length - 1 && <div className={`h-1 w-16 mt-3 transition-colors ${index < currentStep ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-183"></div>}
                </div>)}
            </div>
          </div>

          {steps[currentStep].component}
        </div>
      </div>
    </div>;
}